/* Email a customer without leaving Shopify: pick them, read what you have said to each other (from Gmail), write or
 * reply. Their quotes sit alongside, one click from a new one. */
import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { listQuotes, searchCustomers } from '../lib/admin.js';
import { money, quoteState, daysAgo } from '../lib/quote.js';
import { renderPlainEmail } from '../lib/email.js';
import { idOf, useApp } from '../lib/app-state.js';
import { busy, displayName, getConversation, getMessage, sendMail, toast } from '../lib/server.js';
import { CustomerPicker } from './Pickers.jsx';

function when(iso) {
  const d = new Date(iso);
  const today = new Date();
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
}

function Message({ m, open, onToggle, onReply, mailbox }) {
  const [full, setFull] = useState(null);
  useEffect(() => {
    if (open && !full) getMessage(m.id).then(setFull).catch((e) => toast(e.message, true));
  }, [open]);
  return (
    <s-box padding="base" borderRadius="base" border="base" background={m.outgoing ? 'subdued' : 'base'}>
      <s-stack gap="small">
        <s-clickable onClick={onToggle} accessibilityLabel={open ? 'Collapse' : 'Expand'}>
          <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
            <s-stack gap="none">
              <s-stack direction="inline" gap="small" alignItems="center">
                <s-text type="strong">{m.outgoing ? 'You' : displayName(m.from)}</s-text>
                {m.unread && <s-badge tone="info">New</s-badge>}
                {m.outgoing && <s-text color="subdued">to {displayName(m.to)}</s-text>}
              </s-stack>
              <s-text color="subdued">{m.subject || '(no subject)'}{open ? '' : ` — ${m.snippet}`}</s-text>
            </s-stack>
            <s-text color="subdued">{when(m.date)}</s-text>
          </s-grid>
        </s-clickable>
        {open && (
          <s-stack gap="small">
            {!full ? <s-spinner accessibilityLabel="Loading message" /> : <pre class="mail-body">{full.text}</pre>}
            {full?.attachments > 0 && <s-text color="subdued">{full.attachments} attachment{full.attachments > 1 ? 's' : ''} — open in Gmail to see {full.attachments > 1 ? 'them' : 'it'}.</s-text>}
            <s-stack direction="inline" gap="small">
              <s-button onClick={() => onReply(m)} icon="reply">Reply</s-button>
              <s-button variant="tertiary" href={`https://mail.google.com/mail/?authuser=${encodeURIComponent(mailbox || '')}#all/${m.threadId}`} target="_blank">Open in Gmail</s-button>
            </s-stack>
          </s-stack>
        )}
      </s-stack>
    </s-box>
  );
}

export default function EmailPage() {
  const app = useApp();
  const { url, route } = useLocation();
  const gmail = app.config?.gmail?.connected;
  const [who, setWho] = useState(null); // {email, name, customer?}
  const [thread, setThread] = useState(null);
  const [threadErr, setThreadErr] = useState('');
  const [quotes, setQuotes] = useState([]);
  const [open, setOpen] = useState(null);
  const [draft, setDraft] = useState({ subject: '', message: '', threadId: '', inReplyTo: '', references: '' });
  const [sending, setSending] = useState(false);

  /* /email?email=x or /email?customer=123 */
  useEffect(() => {
    const p = new URL(url, location.origin).searchParams;
    const e = p.get('email');
    if (e) pick({ email: e.toLowerCase(), name: e });
  }, [url]);

  const pick = async (w) => {
    setWho(w);
    setThread(null);
    setThreadErr('');
    setOpen(null);
    setDraft({ subject: '', message: `Hi ${(w.customer?.firstName || w.name || '').split(' ')[0] || 'there'},\n\n`, threadId: '', inReplyTo: '', references: '' });
    if (!w.customer) {
      searchCustomers(`email:${w.email}`).then((r) => r[0] && setWho((x) => (x?.email === w.email ? { ...x, customer: r[0], name: r[0].displayName } : x))).catch(() => {});
    }
    listQuotes({ search: `email:${w.email}` }).then((r) => setQuotes(r.quotes)).catch(() => setQuotes([]));
    if (gmail) loadThread(w.email);
  };

  const loadThread = (email) =>
    getConversation(email)
      .then((r) => { setThread(r.messages); if (r.messages[0]) setOpen(r.messages[0].id); })
      .catch((e) => { setThreadErr(e.message); setThread([]); });

  const reply = (m) => {
    const subject = /^re:/i.test(m.subject) ? m.subject : `Re: ${m.subject}`;
    setDraft({ subject, message: draft.message, threadId: m.threadId, inReplyTo: m.messageId, references: [m.references, m.messageId].filter(Boolean).join(' ') });
    setTimeout(() => document.getElementById('composer')?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  const send = async () => {
    if (!draft.subject.trim()) return toast('Add a subject', true);
    if (!draft.message.trim()) return toast('Write a message', true);
    setSending(true);
    busy(true);
    try {
      const body = renderPlainEmail({ message: draft.message, settings: app.settings });
      await sendMail({ to: who.email, subject: draft.subject.trim(), text: body.text, html: body.html, threadId: draft.threadId || undefined, inReplyTo: draft.inReplyTo || undefined, references: draft.references || undefined });
      toast(`Sent to ${who.email}`);
      setDraft({ subject: '', message: '', threadId: '', inReplyTo: '', references: '' });
      setTimeout(() => loadThread(who.email), 1200);
    } catch (e) {
      toast(e.message, true);
    } finally {
      setSending(false);
      busy(false);
    }
  };

  return (
    <s-page heading={who ? `Email ${who.name || who.email}` : 'Email a customer'} inlineSize="base">
      {who && <s-link slot="breadcrumb-actions" href="/email" onClick={(e) => { e.preventDefault(); setWho(null); route('/email'); }}>Email</s-link>}
      {who?.customer && (
        <s-button slot="secondary-actions" href={`/quotes/new?customer=${idOf(who.customer.id)}`}>New quote for them</s-button>
      )}

      {!gmail && (
        <s-banner tone="warning" heading="Connect Gmail to email customers from here">
          <s-paragraph>Once connected, emails go out from your mailbox and the whole conversation shows on this page.</s-paragraph>
          <s-button slot="secondary-actions" href="/settings">Settings</s-button>
        </s-banner>
      )}

      {!who && (
        <s-section heading="Who are you writing to?">
          <CustomerPicker autoFocus onPick={(c) => (c.email ? pick({ email: c.email.toLowerCase(), name: c.displayName, customer: c }) : toast(`${c.displayName} has no email address on file`, true))} />
        </s-section>
      )}

      {who && gmail && (
        <s-section heading={draft.threadId ? 'Reply' : 'New email'}>
          <div id="composer" />
          <s-stack gap="base">
            <s-text color="subdued">To {who.name && who.name !== who.email ? `${who.name} <${who.email}>` : who.email} · from {app.config.gmail.email}</s-text>
            <s-text-field label="Subject" value={draft.subject} onInput={(e) => setDraft({ ...draft, subject: e.currentTarget.value })} />
            <s-text-area label="Message" rows={7} value={draft.message} onInput={(e) => setDraft({ ...draft, message: e.currentTarget.value })} details="Your signature is added automatically." />
            <s-stack direction="inline" gap="base">
              <s-button variant="primary" loading={sending} onClick={send}>Send</s-button>
              {draft.threadId && <s-button variant="tertiary" onClick={() => setDraft({ ...draft, threadId: '', inReplyTo: '', references: '', subject: '' })}>New email instead</s-button>}
            </s-stack>
          </s-stack>
        </s-section>
      )}

      {who && gmail && (
        <s-section heading="Conversation">
          {thread === null && <s-spinner accessibilityLabel="Loading emails" />}
          {threadErr && <s-text tone="critical">{threadErr}</s-text>}
          {thread && thread.length === 0 && !threadErr && <s-paragraph color="subdued">No emails with {who.email} yet.</s-paragraph>}
          {thread && thread.length > 0 && (
            <s-stack gap="small">
              {thread.map((m) => (
                <Message key={m.id} m={m} open={open === m.id} onToggle={() => setOpen(open === m.id ? null : m.id)} onReply={reply} mailbox={app.config.gmail.email} />
              ))}
            </s-stack>
          )}
        </s-section>
      )}

      {who && (
        <s-section slot="aside" heading="Quotes">
          {quotes.length === 0 ? (
            <s-text color="subdued">None yet.</s-text>
          ) : (
            <s-stack gap="small">
              {quotes.slice(0, 8).map((q) => {
                const st = quoteState(q);
                return (
                  <s-clickable key={q.id} onClick={() => route(`/quotes/${idOf(q.id)}`)} padding="small-200" borderRadius="base">
                    <s-grid gridTemplateColumns="1fr auto" gap="small" alignItems="center">
                      <s-stack gap="none">
                        <s-text type="strong">{q.name} · {money(q.total)}</s-text>
                        <s-text color="subdued">{daysAgo(q.updatedAt)}</s-text>
                      </s-stack>
                      <s-badge tone={st.tone}>{st.label}</s-badge>
                    </s-grid>
                  </s-clickable>
                );
              })}
            </s-stack>
          )}
          {who.customer && (
            <s-box paddingBlockStart="base">
              <s-button href={`/quotes/new?customer=${idOf(who.customer.id)}`} icon="plus">New quote</s-button>
            </s-box>
          )}
        </s-section>
      )}
    </s-page>
  );
}

